import { useCallback, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Modal } from '../../components/Modal';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  approveDisplayPairing,
  deleteDisplayDevice,
  listDisplayDevices,
  renameDisplayDevice,
  type DisplayDevice,
} from '../../lib/api';
import { PS, T } from '../../lib/theme';
import { formatLocal } from '../../lib/time';
import { orderByDateDescending } from '../../lib/admin-list-order';

interface RenameDraft {
  device: DisplayDevice;
  label: string;
}

interface RenameConfirmation {
  device: DisplayDevice;
  label: string;
}

function formatTimestamp(value: string | null): string {
  return value ? formatLocal(value, 'dd MMM yyyy, HH:mm') : 'Never';
}

function safeError(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function DisplayDevicesPage() {
  const { push } = useToast();
  const loader = useCallback((signal: AbortSignal) => listDisplayDevices(signal), []);
  const devices = useApi(loader);
  const orderedDevices = useMemo(
    () => orderByDateDescending(devices.data ?? [], (device) => ({ date: device.created_at, id: device.id })),
    [devices.data],
  );
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [approving, setApproving] = useState(false);
  const [renameDraft, setRenameDraft] = useState<RenameDraft | null>(null);
  const [renameConfirmation, setRenameConfirmation] = useState<RenameConfirmation | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DisplayDevice | null>(null);

  const approve = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedCode = code.trim().toUpperCase();
    const normalizedLabel = label.trim();
    if (!normalizedCode || !normalizedLabel) {
      push('error', 'Enter a pairing code and display name.');
      return;
    }

    setApproving(true);
    try {
      await approveDisplayPairing({ code: normalizedCode, label: normalizedLabel });
      setCode('');
      setLabel('');
      push('success', 'Display device approved.');
      devices.refetch();
    } catch (err) {
      push('error', safeError(err, 'Could not approve this pairing code.'));
    } finally {
      setApproving(false);
    }
  };

  const continueRename = (event: FormEvent) => {
    event.preventDefault();
    if (!renameDraft) return;
    const nextLabel = renameDraft.label.trim();
    if (!nextLabel) {
      push('error', 'Enter a display name.');
      return;
    }
    if (nextLabel === renameDraft.device.label) {
      setRenameDraft(null);
      return;
    }
    setRenameConfirmation({ device: renameDraft.device, label: nextLabel });
    setRenameDraft(null);
  };

  const confirmRename = async () => {
    if (!renameConfirmation) return;
    const { device, label: nextLabel } = renameConfirmation;
    try {
      await renameDisplayDevice(device.id, { label: nextLabel });
      setRenameConfirmation(null);
      push('success', 'Display device renamed.');
      devices.refetch();
    } catch (err) {
      push('error', safeError(err, 'Could not rename this display device.'));
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      await deleteDisplayDevice(removeTarget.id);
      setRemoveTarget(null);
      push('success', 'Display device removed.');
      devices.refetch();
    } catch (err) {
      push('error', safeError(err, 'Could not remove this display device.'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Display devices</h1>
        <p style={{ ...T.caption, color: PS.textSec, margin: '4px 0 0' }}>
          Approve pairing codes shown by lobby TVs, then manage each TV independently.
        </p>
      </header>

      <section style={cardStyle}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Approve a display</h2>
        <form
          onSubmit={(event) => void approve(event)}
          style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}
        >
          <label style={fieldStyle}>
            <span style={T.label}>Pairing code</span>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              maxLength={8}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="AB12CD34"
              style={inputStyle}
              disabled={approving}
            />
          </label>
          <label style={fieldStyle}>
            <span style={T.label}>Display name</span>
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              maxLength={120}
              placeholder="Main lobby TV"
              style={inputStyle}
              disabled={approving}
            />
          </label>
          <Button type="submit" disabled={approving}>
            {approving ? 'Approving…' : 'Approve device'}
          </Button>
        </form>
      </section>

      <section style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ ...T.subheading, margin: 0 }}>Paired displays</h2>
          <Button variant="secondary" onClick={devices.refetch} disabled={devices.loading}>
            Refresh
          </Button>
        </div>

        {devices.loading && <p style={{ ...T.caption, margin: 0, color: PS.textSec }}>Loading displays…</p>}
        {devices.error && (
          <div style={errorStyle} role="alert">
            <span>{safeError(devices.error, 'Could not load display devices.')}</span>
            <Button variant="secondary" onClick={devices.refetch}>Retry</Button>
          </div>
        )}
        {!devices.loading && !devices.error && (devices.data?.length ?? 0) === 0 && (
          <p style={{ ...T.caption, margin: 0, color: PS.textSec }}>No display devices have been approved yet.</p>
        )}
        {!devices.loading && !devices.error && (devices.data?.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${PS.border}` }}>
                  <th style={th}>Display</th>
                  <th style={th}>Created</th>
                  <th style={th}>Last used</th>
                  <th style={th} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {orderedDevices.map((device) => {
                  return (
                    <tr key={device.id} style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                      <td style={td}>{device.label}</td>
                      <td style={td}>{formatTimestamp(device.created_at)}</td>
                      <td style={td}>{formatTimestamp(device.last_used_at)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Button
                            variant="secondary"
                            onClick={() => setRenameDraft({ device, label: device.label })}
                          >
                            Rename
                          </Button>
                          <Button variant="danger" onClick={() => setRemoveTarget(device)}>
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={renameDraft !== null}
        onClose={() => setRenameDraft(null)}
        title="Rename display device"
      >
        <form onSubmit={continueRename} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={fieldStyle}>
            <span style={T.label}>Display name</span>
            <input
              value={renameDraft?.label ?? ''}
              onChange={(event) => setRenameDraft((current) => current && { ...current, label: event.target.value })}
              maxLength={120}
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button type="button" variant="secondary" onClick={() => setRenameDraft(null)}>Cancel</Button>
            <Button type="submit">Continue</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={renameConfirmation !== null}
        title="Confirm rename"
        message={`Rename “${renameConfirmation?.device.label ?? ''}” to “${renameConfirmation?.label ?? ''}”?`}
        confirmLabel="Rename"
        onConfirm={confirmRename}
        onCancel={() => setRenameConfirmation(null)}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove display device"
        message={`Remove “${removeTarget?.label ?? ''}”? This TV will be signed out and deleted. It can be paired again with a new code.`}
        confirmLabel="Remove device"
        destructive
        onConfirm={confirmRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}

const cardStyle: CSSProperties = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  flex: '1 1 220px',
};

const inputStyle: CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  padding: '10px 12px',
  minHeight: 42,
  ...T.ui,
};

const errorStyle: CSSProperties = {
  ...T.caption,
  padding: 12,
  background: PS.errorBg,
  border: `1px solid ${PS.errorBorder}`,
  color: PS.errorText,
  borderRadius: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const th: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  ...T.label,
  color: PS.textSec,
};

const td: CSSProperties = {
  padding: '10px 12px',
  color: PS.nearBlack,
  verticalAlign: 'middle',
  ...T.ui,
};
